/**
 * Staff-only management of a tenant's platform-owned (managed) API keys.
 *
 * These keys are owned by iWarranty and hidden from the tenant's own Settings
 * -> API Keys surface. Staff create them here, scoped to an organisation role,
 * and can reveal/revoke the token (which the tenant cannot). Backed by
 * /api/staff/tenants/<uuid>/api-keys/.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Copy, KeyRound, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import api from '@/admin/lib/api'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting'

interface ManagedKey {
  uuid: string
  name: string
  description: string
  masked_token: string
  status: 'active' | 'revoked'
  organisation: { uuid: string; name: string }
  role_name: string
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
}

interface OrgOption {
  uuid: string
  name: string
}

interface ScopeOption {
  value: string // `${orgUuid}:${roleUuid}`
  label: string
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function StaffApiKeysSection({
  tenantUuid,
  organisations,
}: {
  tenantUuid: string
  organisations: OrgOption[]
}) {
  const [keys, setKeys] = useState<ManagedKey[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [reveal, setReveal] = useState<{
    name: string
    rawToken?: string
    keyUuid?: string
  } | null>(null)
  const [revoking, setRevoking] = useState<ManagedKey | null>(null)
  const { locale } = useFormatters()

  const base = `/staff/tenants/${tenantUuid}/api-keys`

  const fetchKeys = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`${base}/`)
      setKeys(res.data ?? [])
    } catch (err) {
      toastApiError(err, 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }, [base])

  useEffect(() => {
    fetchKeys()
  }, [fetchKeys])

  async function handleRevoke() {
    if (!revoking) return
    try {
      await api.post(`${base}/${revoking.uuid}/revoke/`)
      toast.success('API key revoked')
      setRevoking(null)
      fetchKeys()
    } catch (err) {
      toastApiError(err, 'Failed to revoke API key')
    }
  }

  const columns = useMemo<DataTableColumn<ManagedKey>[]>(
    () => [
      { id: 'name', header: 'Name', cell: (k) => k.name },
      {
        id: 'organisation',
        header: 'Organisation',
        cell: (k) => (
          <span className="text-[var(--ink-3)]">{k.organisation.name}</span>
        ),
      },
      {
        id: 'role',
        header: 'Scope (role)',
        cell: (k) => (
          <span className="text-[var(--ink-3)]">{k.role_name || '-'}</span>
        ),
      },
      {
        id: 'token',
        header: 'Token',
        cell: (k) => (
          <span className="font-mono text-[11px] text-[var(--ink-3)]">
            {k.masked_token}...
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (k) => (
          <Badge variant={k.status === 'active' ? 'default' : 'destructive'}>
            {k.status === 'active' ? 'Active' : 'Revoked'}
          </Badge>
        ),
      },
      { id: 'created', header: 'Created', cell: (k) => formatDate(k.created_at, locale) },
      {
        id: 'actions',
        header: '',
        cell: (k) =>
          k.status === 'active' ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setReveal({ name: k.name, keyUuid: k.uuid })}
              >
                Reveal
              </Button>
              <Button
                variant="ghost"
                size="xs"
                className="text-[var(--status-error)]"
                onClick={() => setRevoking(k)}
              >
                Revoke
              </Button>
            </div>
          ) : null,
      },
    ],
    [locale],
  )

  return (
    <section className="mt-10">
      <div className="mb-5 flex items-center justify-between border-b-[0.5px] border-[var(--rule)] pb-3">
        <h3 className="clara-section-label flex items-center gap-2">
          <KeyRound className="h-3.5 w-3.5" />
          Platform API Keys
        </h3>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create Key
        </Button>
      </div>

      <p className="mb-4 text-[12px] text-[var(--ink-3)]">
        iWarranty-owned keys for this tenant - hidden from the tenant's own API
        Keys settings. Scoped to an organisation role; only staff can reveal or
        revoke them.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-[12px] text-[var(--ink-3)]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading keys...
        </div>
      ) : keys.length === 0 ? (
        <p className="py-6 text-center text-[12px] text-[var(--ink-3)]">
          No platform keys for this tenant yet.
        </p>
      ) : (
        <DataTable
          tableId="staff-platform-api-keys"
          columns={columns}
          data={keys}
          rowKey={(k) => k.uuid}
        />
      )}

      <CreateManagedKeyDialog
        open={createOpen}
        tenantUuid={tenantUuid}
        organisations={organisations}
        onClose={() => setCreateOpen(false)}
        onCreated={(name, rawToken) => {
          setCreateOpen(false)
          setReveal({ name, rawToken })
          fetchKeys()
        }}
      />

      <RevealManagedKeyDialog
        reveal={reveal}
        base={base}
        onClose={() => setReveal(null)}
      />

      <Dialog
        open={!!revoking}
        onOpenChange={(v) => {
          if (!v) setRevoking(null)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revoke API key</DialogTitle>
            <DialogDescription>
              Revoke{' '}
              <span className="font-medium text-[var(--ink)]">
                {revoking?.name}
              </span>
              ? Any client using this token (e.g. the voice agent) will stop
              working immediately. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevoke}>
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function CreateManagedKeyDialog({
  open,
  tenantUuid,
  organisations,
  onClose,
  onCreated,
}: {
  open: boolean
  tenantUuid: string
  organisations: OrgOption[]
  onClose: () => void
  onCreated: (name: string, rawToken: string) => void
}) {
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([])
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Build one "organisation -> role" picker so there is no cascading select.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setOptionsLoading(true)
    Promise.all(
      organisations.map((org) =>
        api
          .get(`/staff/tenants/${tenantUuid}/api-keys/roles/`, {
            params: { organisation: org.uuid },
          })
          .then((res) =>
            (res.data ?? []).map(
              (r: { uuid: string; name: string }): ScopeOption => ({
                value: `${org.uuid}:${r.uuid}`,
                label:
                  organisations.length > 1
                    ? `${org.name} - ${r.name}`
                    : r.name,
              }),
            ),
          )
          .catch(() => [] as ScopeOption[]),
      ),
    ).then((lists) => {
      if (cancelled) return
      setScopeOptions(lists.flat())
      setOptionsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open, tenantUuid, organisations])

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'scope',
        label: 'Organisation & role',
        value: scopeOptions[0]?.value ?? '',
        options: scopeOptions,
        validationSchema: z.string().min(1, 'Select an organisation and role'),
      },
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        placeholder: 'e.g., Voice Agent (Retell)',
        value: '',
        validationSchema: z.string().trim().min(1, 'Name is required'),
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Optional',
        value: '',
        validationSchema: z.string().trim(),
      },
    ],
    [scopeOptions],
  )

  const { Form, ref } = useForm(
    { name: 'staff-create-api-key', submit: 'footer', fields },
    async (rawValues) => {
      const values = rawValues as { scope: string; name: string; description: string }
      const [organisationUuid, roleUuid] = values.scope.split(':')
      setSaving(true)
      try {
        const res = await api.post(`/staff/tenants/${tenantUuid}/api-keys/`, {
          organisation_uuid: organisationUuid,
          role_uuid: roleUuid,
          name: values.name.trim(),
          description: values.description.trim(),
        })
        onCreated(values.name.trim(), res.data.raw_token as string)
      } catch (err) {
        toastApiError(err, 'Failed to create API key')
      } finally {
        setSaving(false)
      }
    },
    [open, scopeOptions],
  )

  function handleClose() {
    ref.current?.resetForm()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create platform API key</DialogTitle>
          <DialogDescription>
            Owned by iWarranty, hidden from the tenant. Scoped to the selected
            organisation role.
          </DialogDescription>
        </DialogHeader>
        {optionsLoading ? (
          <div className="flex items-center justify-center py-8 text-[12px] text-[var(--ink-3)]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading roles...
          </div>
        ) : scopeOptions.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-[var(--ink-3)]">
            No roles available for this tenant's organisations.
          </p>
        ) : (
          <Form className="space-y-4" />
        )}
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="staff-create-api-key"
            disabled={saving || optionsLoading || scopeOptions.length === 0}
          >
            {saving && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RevealManagedKeyDialog({
  reveal,
  base,
  onClose,
}: {
  reveal: { name: string; rawToken?: string; keyUuid?: string } | null
  base: string
  onClose: () => void
}) {
  return (
    <Dialog
      open={!!reveal}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <DialogContent className="max-w-lg">
        {reveal && (
          <RevealManagedKeyContent
            key={reveal.keyUuid ?? 'new'}
            reveal={reveal}
            base={base}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function RevealManagedKeyContent({
  reveal,
  base,
  onClose,
}: {
  reveal: { name: string; rawToken?: string; keyUuid?: string }
  base: string
  onClose: () => void
}) {
  // Initialise from the raw token (post-create) so no effect setState is needed.
  const [token, setToken] = useState<string | null>(reveal.rawToken ?? null)
  const [loading, setLoading] = useState(!reveal.rawToken && !!reveal.keyUuid)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (reveal.rawToken || !reveal.keyUuid) return
    let cancelled = false
    api
      .post(`${base}/${reveal.keyUuid}/reveal/`)
      .then((res) => {
        if (!cancelled) setToken(res.data.api_key as string)
      })
      .catch((err) => {
        if (cancelled) return
        toastApiError(err, 'Failed to reveal API key')
        onClose()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reveal, base, onClose])

  function handleCopy() {
    if (!token) return
    navigator.clipboard.writeText(token)
    setCopied(true)
    toast.success('API key copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>API Key</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <p className="text-[14px] font-medium text-[var(--ink)]">{reveal.name}</p>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : token ? (
          <div className="rounded-lg bg-[var(--bg-subtle)] p-4">
            <code className="block break-all font-mono text-[13px] text-[var(--ink)]">
              {token}
            </code>
          </div>
        ) : null}
        <p className="text-[11px] text-[var(--ink-3)]">
          Wire this into the voice agent (Authorization: Token ...). You can
          reveal it again here at any time.
        </p>
      </div>
      <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button onClick={handleCopy} disabled={!token}>
          {copied ? (
            <Check className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Copy className="mr-1.5 h-3.5 w-3.5" />
          )}
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Button>
      </DialogFooter>
    </>
  )
}
